import type { MedplumClient } from "@medplum/core";
import type {
  CodeableConcept,
  Provenance,
  ProvenanceAgent,
  ProvenanceEntity,
} from "@medplum/fhirtypes";
import {
  type CreateResourceOptions,
  FhirResource,
  type SearchOptions,
  type SubscriptionCallback,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceData, ResourceRef } from "@vibrant/models/fhir/fhir-resource-types";
import { time } from "@/lib/time";

const PROVENANCE_PARTICIPANT_TYPE_SYSTEM =
  "http://terminology.hl7.org/CodeSystem/provenance-participant-type" as const;
const SOURCE_ENTITY_ROLE: NonNullable<ProvenanceEntity["role"]> = "source";
const MAX_PROVENANCE_APPEND_RETRIES = 3;

export interface SourceCreateRequest {
  for: ResourceRef;
  date?: string;
  author?: ResourceRef;
  assembler?: ResourceRef;
  sources: ResourceRef[];
  activity?: CodeableConcept;
  occurredDate?: string;
}

export interface SourceSearchQuery {
  id?: string;
  target?: ResourceRef;
  source?: ResourceRef;
  recorded?: string;
}

export interface SourceSearchTargetsBySourceRequest {
  source: ResourceRef;
  targetResourceType?: string;
  /**
   * Page size hint for server pagination (not a total-results cap).
   */
  limit?: number;
}

/**
 * Source wraps FHIR Provenance and centralizes source-to-target lineage linking.
 *
 * It models the many-to-many relationship between generated resources (target) and
 * originating artifacts (entity.what), while enforcing consistent agent attribution.
 */
export class Source extends FhirResource<Provenance> {
  static readonly ResourceType = "Provenance";

  //------------------------------------------------------------------------------------------------
  //                                     CLASS METHODS (STATIC)
  //------------------------------------------------------------------------------------------------
  static toFhirQuery(query: SourceSearchQuery): Record<string, string | undefined> {
    return {
      _id: query.id?.trim() || undefined,
      target: Source.normalizeReference(query.target),
      entity: Source.normalizeReference(query.source),
      recorded: query.recorded?.trim() || undefined,
    };
  }

  static toFhirCreateObject(data: SourceCreateRequest): ResourceData<Provenance> {
    const targetReference = Source.normalizeReference(data.for);
    if (!targetReference) {
      throw new Error("Source.create requires a non-empty target reference");
    }

    const sourceReferences = Source.normalizeSourceReferences(data.sources);
    if (sourceReferences.length === 0) {
      throw new Error("Source.create requires at least one source reference");
    }

    const agent = Source.toProvenanceAgent({
      author: data.author,
      assembler: data.assembler,
    });
    if (!agent) {
      throw new Error("Source.create requires either author or assembler");
    }

    return {
      target: [{ reference: targetReference }],
      recorded: data.date ?? time().toISOString(),
      ...(data.occurredDate ? { occurredDateTime: data.occurredDate } : {}),
      ...(data.activity ? { activity: data.activity } : {}),
      agent: [agent],
      entity: sourceReferences.map((reference) => ({
        role: SOURCE_ENTITY_ROLE,
        what: { reference },
      })),
    };
  }

  /**
   * Creates or updates (idempotently) a provenance record for a target.
   *
   * Returns `undefined` when source references normalize to an empty set so callers can
   * treat "no source context" as a no-op.
   */
  static async create(
    medplum: MedplumClient,
    data: SourceCreateRequest,
    options?: CreateResourceOptions,
  ): Promise<Source | undefined> {
    const targetReference = Source.normalizeReference(data.for);
    if (!targetReference) {
      throw new Error("Source.create requires a non-empty target reference");
    }

    const sourceReferences = Source.normalizeSourceReferences(data.sources);
    if (sourceReferences.length === 0) {
      return undefined;
    }

    const existing = await Source.searchOne(medplum, {
      target: { reference: targetReference },
    });

    if (existing) {
      for (let attempt = 0; attempt < MAX_PROVENANCE_APPEND_RETRIES; attempt++) {
        const latest = await Source.searchOne(medplum, {
          target: { reference: targetReference },
        });
        if (!latest) {
          break;
        }

        const latestResource = latest.serialize();
        const latestEntityReferences = new Set(
          (latestResource.entity ?? [])
            .map((entity) => entity.what?.reference)
            .filter((reference): reference is string => Boolean(reference)),
        );

        const newEntities = sourceReferences
          .filter((reference) => !latestEntityReferences.has(reference))
          .map((reference) => ({
            role: SOURCE_ENTITY_ROLE,
            what: { reference },
          }));

        if (newEntities.length === 0) {
          return latest;
        }

        // Existing provenance keeps original attribution/timing metadata.
        // New writes append only missing source entities.
        await Source.patchResource(medplum, {
          id: latest.id,
          patch: {
            entity: [...(latestResource.entity ?? []), ...newEntities],
          },
        });

        const latestAfterPatch = await Source.searchOne(medplum, {
          target: { reference: targetReference },
        });
        if (!latestAfterPatch) {
          continue;
        }

        if (Source.hasAllEntityReferences(latestAfterPatch.serialize(), sourceReferences)) {
          return latestAfterPatch;
        }
      }

      const latest = await Source.searchOne(medplum, {
        target: { reference: targetReference },
      });
      if (latest) {
        return latest;
      }
    }

    const createPayload = Source.toFhirCreateObject({
      ...data,
      for: { reference: targetReference },
      sources: sourceReferences.map((reference) => ({ reference })),
      date: data.date ?? time().toISOString(),
    });
    const created = await Source.createResource(medplum, createPayload, options);
    return new Source(created);
  }

  static async createFromProvenance(
    medplum: MedplumClient,
    provenance: Provenance,
  ): Promise<Source> {
    const { id: _id, resourceType: _resourceType, ...resourceData } = provenance;
    const created = await Source.createResource(medplum, resourceData);
    return new Source(created);
  }

  static async updateFromProvenance(
    medplum: MedplumClient,
    provenance: Provenance,
  ): Promise<Source> {
    if (!provenance.id) {
      throw new Error("Source.updateFromProvenance requires provenance.id");
    }

    const { id, resourceType: _resourceType, ...patchData } = provenance;
    const updated = await Source.patchResource(medplum, { id, patch: patchData });
    return new Source(updated);
  }

  static async search(
    medplum: MedplumClient,
    query: SourceSearchQuery,
    options?: SearchOptions,
  ): Promise<Source[]> {
    const resources = await Source.searchResources(medplum, Source.toFhirQuery(query), options);
    return resources.map((resource) => new Source(resource));
  }

  static async searchOne(
    medplum: MedplumClient,
    query: SourceSearchQuery,
    options?: SearchOptions,
  ): Promise<Source | undefined> {
    const resource = await Source.searchOneResource(medplum, Source.toFhirQuery(query), options);
    return resource ? new Source(resource) : undefined;
  }

  static async searchTargetRefsBySource(
    medplum: MedplumClient,
    query: SourceSearchTargetsBySourceRequest,
  ): Promise<string[]> {
    const sourceReference = Source.normalizeReference(query.source);
    if (!sourceReference) {
      return [];
    }

    const pageSize = Math.min(Math.max(query.limit ?? 200, 1), 1000);
    const targetReferences = new Set<string>();

    let offset = 0;
    while (true) {
      const page = await Source.search(
        medplum,
        {
          source: { reference: sourceReference },
        },
        {
          limit: pageSize,
          offset,
        },
      );

      if (page.length === 0) {
        break;
      }

      for (const source of page) {
        for (const target of source.serialize().target ?? []) {
          const targetReference = target.reference;
          if (!targetReference) {
            continue;
          }
          if (
            query.targetResourceType &&
            !targetReference.startsWith(`${query.targetResourceType}/`)
          ) {
            continue;
          }
          targetReferences.add(targetReference);
        }
      }

      if (page.length < pageSize) {
        break;
      }

      offset += pageSize;
    }

    return Array.from(targetReferences);
  }

  static extractReferenceId(reference: string, expectedResourceType?: string): string | undefined {
    const parsed = Source.parseReference(reference);
    if (!parsed) {
      return undefined;
    }
    if (expectedResourceType && parsed.resourceType !== expectedResourceType) {
      return undefined;
    }
    return parsed.id;
  }

  static subscribe(
    medplum: MedplumClient,
    query: SourceSearchQuery,
    callback: SubscriptionCallback<Source>,
  ) {
    return Source.subscribeToResource(medplum, Source.toFhirQuery(query), (resource, metadata) =>
      callback(new Source(resource), metadata),
    );
  }

  //------------------------------------------------------------------------------------------------
  //                                           HELPERS
  //------------------------------------------------------------------------------------------------
  private static toProvenanceAgent({
    author,
    assembler,
  }: {
    author?: ResourceRef;
    assembler?: ResourceRef;
  }): ProvenanceAgent | undefined {
    const authorReference = Source.normalizeReference(author);
    if (authorReference) {
      return {
        type: {
          coding: [{ system: PROVENANCE_PARTICIPANT_TYPE_SYSTEM, code: "author" }],
        },
        who: { reference: authorReference },
      };
    }

    const assemblerReference = Source.normalizeReference(assembler);
    if (!assemblerReference) {
      return undefined;
    }

    return {
      type: {
        coding: [{ system: PROVENANCE_PARTICIPANT_TYPE_SYSTEM, code: "assembler" }],
      },
      who: { reference: assemblerReference },
    };
  }

  private static normalizeSourceReferences(sources: ResourceRef[] = []): string[] {
    const unique = new Set<string>();
    for (const source of sources) {
      const reference = Source.normalizeReference(source);
      if (!reference) {
        continue;
      }
      unique.add(reference);
    }

    return Array.from(unique);
  }

  private static normalizeReference(reference?: ResourceRef): string | undefined {
    const value = reference?.reference?.trim();
    if (!value) {
      return undefined;
    }
    return value;
  }

  private static hasAllEntityReferences(resource: Provenance, sourceReferences: string[]): boolean {
    const existingEntityReferences = new Set(
      (resource.entity ?? [])
        .map((entity) => entity.what?.reference)
        .filter((reference): reference is string => Boolean(reference)),
    );
    return sourceReferences.every((reference) => existingEntityReferences.has(reference));
  }

  static parseReference(reference: string): { resourceType: string; id: string } | undefined {
    const trimmed = reference.trim();
    if (!trimmed) {
      return undefined;
    }

    let path = trimmed;
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
      try {
        path = new URL(trimmed).pathname;
      } catch {
        return undefined;
      }
    }

    const segments = path.split("/").filter(Boolean);
    const historyIndex = segments.indexOf("_history");
    const pathSegments = historyIndex >= 0 ? segments.slice(0, historyIndex) : segments;
    if (pathSegments.length < 2) {
      return undefined;
    }

    const resourceType = pathSegments[pathSegments.length - 2];
    const id = pathSegments[pathSegments.length - 1];
    if (!resourceType || !id) {
      return undefined;
    }

    return { resourceType, id };
  }
}
